import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Trash, ShieldCheck, Prohibit, FileText, Megaphone, Camera, User, Lock } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import NotificationPreferencesPanel from '@/components/NotificationPreferencesPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const LEGAL_ITEMS = [
  { key: 'privacidade', label: 'Política de Privacidade', desc: 'Como os teus dados são tratados', icon: ShieldCheck, path: '/privacidade' },
  { key: 'cancelamento', label: 'Política de Cancelamento', desc: 'Condições para cancelar ou remarcar', icon: Prohibit, path: '/politica-cancelamento' },
  { key: 'termos', label: 'Termos e Condições', desc: 'Regras gerais de utilização', icon: FileText, path: '/termos-e-condicoes' },
  { key: 'reclamacoes', label: 'Livro de Reclamações', desc: 'Livro de reclamações eletrónico', icon: Megaphone, externalKey: 'livro_reclamacoes_url' },
];

function ChangePasswordSection() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ current: '', newPw: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!form.current || !form.newPw || !form.confirm) { setError('Preenche todos os campos'); return; }
    if (form.newPw.length < 8) { setError('A nova palavra-passe deve ter pelo menos 8 caracteres'); return; }
    if (form.newPw !== form.confirm) { setError('As palavras-passe não coincidem'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/auth/change-password`, { current_password: form.current, new_password: form.newPw });
      toast.success('Palavra-passe alterada com sucesso');
      setForm({ current: '', newPw: '', confirm: '' }); setOpen(false);
    } catch (e) { setError(e.response?.data?.detail || 'Erro ao alterar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="change-password-section">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Segurança</h3>
      {!open ? (
        <button onClick={() => setOpen(true)} className="w-full text-left flex items-center gap-3 p-2 rounded-xl hover:bg-brand-bg transition-colors" data-testid="change-password-btn">
          <Lock size={18} className="text-brand-green" />
          <div><p className="text-sm text-brand-text">Alterar palavra-passe</p><p className="text-[10px] text-brand-muted">Muda a tua palavra-passe de acesso</p></div>
        </button>
      ) : (
        <div className="space-y-3">
          <div><Label className="text-xs text-brand-muted mb-1 block">Palavra-passe actual</Label><Input type="password" value={form.current} onChange={e => setForm({ ...form, current: e.target.value })} className="rounded-xl border-brand-border" data-testid="current-password" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Nova palavra-passe</Label><Input type="password" value={form.newPw} onChange={e => setForm({ ...form, newPw: e.target.value })} placeholder="Mínimo 8 caracteres" className="rounded-xl border-brand-border" data-testid="new-password" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Confirmar nova palavra-passe</Label><Input type="password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} className="rounded-xl border-brand-border" data-testid="confirm-password" /></div>
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleSubmit} disabled={saving} className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-50" data-testid="submit-password-btn">{saving ? 'A alterar...' : 'Alterar palavra-passe'}</button>
            <button onClick={() => { setOpen(false); setError(''); setForm({ current: '', newPw: '', confirm: '' }); }} className="px-4 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DefinicoesPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [profile, setProfile] = useState({ preferred_name: '', birth_date: '', phone: '', client_notes: '' });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [legalInfo, setLegalInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/site-content`).then(({ data }) => setLegalInfo(data.legal_info)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setProfile({
        preferred_name: user.preferred_name || '',
        birth_date: user.birth_date || '',
        phone: user.phone || '',
        client_notes: user.client_notes || '',
      });
    }
  }, [user]);

  const handleAvatarUpload = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/api/my-profile/avatar`, fd);
      toast.success('Foto atualizada');
      if (refreshUser) refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao carregar foto'); }
    finally { setAvatarUploading(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/my-profile`, profile);
      toast.success('Perfil atualizado');
      if (refreshUser) refreshUser();
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== 'ELIMINAR') return;
    setDeleting(true);
    try {
      await axios.post(`${API}/api/account/delete`);
      toast.success('Conta eliminada com sucesso');
      await logout();
      navigate('/login', { replace: true });
    } catch { toast.error('Erro ao eliminar conta'); }
    finally { setDeleting(false); }
  };

  const avatarUrl = user?.avatar_path ? `${API}/api/files/${user.avatar_path}` : null;

  return (
    <div className="fade-in" data-testid="definicoes-page">
      <h2 className="font-heading font-medium text-xl text-brand-text mb-6">Definições</h2>

      {/* Profile photo + name */}
      <div className="bg-white border border-brand-border rounded-2xl p-5 mb-4" data-testid="profile-section">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-4">O teu perfil</h3>

        <div className="flex items-center gap-4 mb-5">
          <div className="relative">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-brand-green/20" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-brand-bg flex items-center justify-center border-2 border-brand-border">
                <User size={24} className="text-brand-placeholder" />
              </div>
            )}
            <label className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-green text-white flex items-center justify-center cursor-pointer hover:bg-brand-green-hover shadow-sm" data-testid="upload-avatar-btn">
              <Camera size={13} />
              <input type="file" accept="image/*" onChange={e => handleAvatarUpload(e.target.files?.[0])} className="hidden" disabled={avatarUploading} />
            </label>
          </div>
          <div>
            <p className="text-sm font-medium text-brand-text">{user?.name}</p>
            <p className="text-xs text-brand-muted">{user?.email}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Nome preferido</Label>
            <Input value={profile.preferred_name} onChange={e => setProfile({ ...profile, preferred_name: e.target.value })} placeholder="Como queres ser tratada" className="rounded-xl border-brand-border" data-testid="preferred-name-input" />
          </div>
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Data de nascimento</Label>
            <Input type="date" value={profile.birth_date} onChange={e => setProfile({ ...profile, birth_date: e.target.value })} className="rounded-xl border-brand-border" data-testid="birth-date-input" />
          </div>
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Telefone</Label>
            <Input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} placeholder="+351..." className="rounded-xl border-brand-border" data-testid="phone-input" />
          </div>
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Notas para a nutricionista</Label>
            <Textarea value={profile.client_notes} onChange={e => setProfile({ ...profile, client_notes: e.target.value })} placeholder="Informações que queiras partilhar..." className="rounded-xl border-brand-border resize-none" rows={3} data-testid="client-notes-input" />
          </div>
          <button onClick={handleSaveProfile} disabled={saving} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="save-profile-btn">
            {saving ? 'A guardar...' : 'Guardar perfil'}
          </button>
        </div>
      </div>

      {/* Change Password */}
      <ChangePasswordSection />

      {/* Water goal */}
      <WaterGoalSection />

      {/* Notification preferences */}
      <div className="mb-4">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Notificações</h3>
        <NotificationPreferencesPanel mode="client" />
      </div>

      {/* Legal */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="legal-section">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Legal</h3>
        <div className="space-y-1">
          {LEGAL_ITEMS.map(({ key, label, desc, icon: Icon, path, externalKey }) => (
            <button
              key={key}
              onClick={() => externalKey ? window.open(legalInfo?.[externalKey] || '', '_blank', 'noopener,noreferrer') : navigate(path)}
              className="w-full text-left flex items-center gap-3 p-2 rounded-xl hover:bg-brand-bg transition-colors"
              data-testid={`legal-${key}-btn`}
            >
              <Icon size={18} className="text-brand-green" />
              <div><p className="text-sm text-brand-text">{label}</p><p className="text-[10px] text-brand-muted">{desc}</p></div>
            </button>
          ))}
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white border border-red-200 rounded-2xl p-4">
        <h3 className="font-heading font-medium text-xs text-red-500 uppercase tracking-wider mb-3">Zona de risco</h3>
        <button onClick={() => setShowDelete(true)} className="w-full text-left flex items-center gap-3 p-2 rounded-xl hover:bg-red-50 transition-colors" data-testid="delete-account-btn">
          <Trash size={18} className="text-red-500" />
          <div><p className="text-sm text-red-600">Eliminar a minha conta</p><p className="text-[10px] text-brand-muted">Apaga todos os teus dados permanentemente</p></div>
        </button>
      </div>

      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent className="rounded-3xl border-red-200">
          <DialogHeader><DialogTitle className="font-heading text-red-600">Eliminar conta</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs text-red-600 font-medium mb-1">Esta ação é irreversível.</p>
              <p className="text-[10px] text-red-500">Todos os teus dados serão permanentemente eliminados.</p>
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1.5">Escreve <b>ELIMINAR</b> para confirmar:</p>
              <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="ELIMINAR" className="rounded-xl border-red-200 text-sm" data-testid="confirm-delete-input" />
            </div>
            <button onClick={handleDeleteAccount} disabled={confirmText !== 'ELIMINAR' || deleting} className="w-full bg-red-500 text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="confirm-delete-btn">
              {deleting ? 'A eliminar...' : 'Eliminar permanentemente'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WaterGoalSection() {
  const [glasses, setGlasses] = useState(8);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/my/water-goal`); setGlasses(data.glasses || 8); setLoaded(true); } catch {}
    })();
  }, []);
  const save = async () => {
    try { await axios.put(`${API}/api/my/water-goal`, { glasses }); toast.success('Objetivo guardado'); } catch { toast.error('Erro'); }
  };
  if (!loaded) return null;
  return (
    <div className="mb-4" data-testid="water-goal-section">
      <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Objetivo de água</h3>
      <div className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-3">
        <Input type="number" min="1" max="20" value={glasses} onChange={e => setGlasses(parseInt(e.target.value) || 8)} className="rounded-xl border-brand-border w-20" data-testid="water-goal-input" />
        <span className="text-xs text-brand-muted">copos por dia</span>
        <button onClick={save} className="ml-auto bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium" data-testid="water-goal-save">Guardar</button>
      </div>
    </div>
  );
}
